import { LAB_CUSTOMER_LANGUAGES } from '@team-milva/lab-models/language'
import { collection, doc, writeBatch } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { generateFirestoreId } from '@/globals/typescript/utils/utils'
import { useAnalysisStore } from '@/stores/analysisStore'
import { useAnalysisTestStore } from '@/stores/analysisTestStore'
import { useTextListStore } from '@/stores/textListStore'
import { useSampleAreaStore } from '@/stores/sampleAreaStore'
import { useTurnaroundTimeStore } from '@/stores/turnaroundTimeStore'
import { useCustomerAccountStore } from '@/stores/customerAccountStore'
import { useCustomerUserStore } from '@/stores/customerUserStore'
import { useDropPointStore } from '@/stores/dropPointStore'
import { useAccountStore } from '@/stores/accountStore'
import { usePricingSegmentStore } from '@/stores/pricingSegmentStore'
import { useInsuranceCustomerAccountStore } from '@/stores/insuranceCustomerAccountStore'
import {
  useReportDescriptionItemStore,
  type ReportDescriptionItem,
} from '@/stores/reportDescriptionItemStore'
import { isDeliveryDelivered } from '@team-milva/lab-utils/orderDeliveries'
import { buildFilledMultiSelectValue } from '@/dev/seed/buildFilledMultiSelectValue'
import {
  ensureInsuranceCustomerFixtures,
  type ISeedInsuranceCustomerFixtures,
} from '@/dev/seed/seedInsuranceCustomerFixtures'
import type { ICustomerRef } from '@team-milva/lab-models/customer'
import { mergePrices } from '@/globals/typescript/utils/customerAccountPricingUtils'
import {
  resolveEffectiveDeal,
  buildAnalysisOrderLines,
} from '@/dev/seed/buildDealAwareOrderLines'
import type { AnalysisTest } from '@/globals/typescript/models/AnalysisTest'
import type { InsuranceCustomerAccountPrice } from '@team-milva/lab-models/insuranceCustomerAccount'
import type {
  ICustomerAccountPrice,
  TCustomerAccountPriceDealEntry,
} from '@team-milva/lab-models/customerAccount'
import type { TPricingSegmentDealEntry } from '@team-milva/lab-models/pricingSegment'
import type {
  IAnalysesTotals,
  IOrderLine,
  IOrderTatGroup,
  IOrderSample,
  IOrderDropPointSnapshot,
  IOrderSampleHandOver,
  IOrderTotals,
  TOrderReportTemplateAssignments,
  TAnalysisResultValue,
  TAnalysisResultSpecialValue,
  TFinalInvoicePayer,
  TOrderLineBillingTarget,
  TOrderDealSnapshot,
  TOrderLabReportStatus,
  TLabReportStatus,
  TPaymentStatus,
  TOrderStatus,
} from '@team-milva/lab-models/resources'

// Never matches a real LabManagementUser — so the 'awaiting-qa' lifecycle
// always shows "Godkend QA" (never "Anmod om QA") regardless of who's seeding
// or reviewing it.
const QA_SEED_PLACEHOLDER_USER_ID = 'dev-seed-qa-requester'

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

type TSampleLifecycle =
  | 'not-registered'
  | 'registered'
  | 'in-progress'
  | 'completed'
  | 'cancelled'
  /**
   * Started, with a pending second-opinion QA request on its analysisTest(s) —
   * intended for a QA-configured test (the prove-based "Asbest General" or the single-select
   * "Asbest Material"),
   * seeded with a real "Nej" AnalysisResult so the modal shows consistent
   * state. `qaRequestedByUserID` is a placeholder that never matches a real user,
   * so "Godkend QA" is immediately testable regardless of who's logged in.
   */
  | 'awaiting-qa'

interface ISeedSampleDef {
  sampleCode: string
  /** Partial Danish name(s) matched against analysis getName('da') */
  analysisNames: string[]
  /** Partial Danish name matched against sample area getName('da') */
  sampleAreaName: string
  /**
   * Fixed-deadline TAT: 'XdHH' — X working days, HH = deadline hour
   * (e.g. '5d17' = 5 working days, frist kl. 17:00)
   * Walk-in TAT: partial Danish name (e.g. 'walk-in asbest')
   */
  tatKey: string
  lifecycle: TSampleLifecycle
  /**
   * Only for 'in-progress': seeds a saved, filled-in draft AnalysisResult for every
   * test of the sample's analyses — what a technician has after pressing "Gem" but not
   * "Færdig". Nothing is completed, approved or sent, so the saved values open (and can
   * be edited) in result entry. Without it an 'in-progress' sample has no results at
   * all. Multi-select tests get `buildFilledMultiSelectValue()`'s richer values so each
   * layout shows what its setup allows (suffixes, overrides, inactive answers, sum).
   */
  hasDraftResults?: boolean
  /**
   * Per-AnalysisTest result overrides for 'value-based' tests, keyed by partial
   * Danish test name (matched like `analysisNames`/`sampleAreaName` above). Lets a
   * blueprint pin specific readings — including 'too-low'/'too-high' — instead of
   * the generic midpoint `buildSeedResultValue()` would otherwise pick.
   */
  valueOverrides?: Record<
    string,
    { value: number | null; specialValue: TAnalysisResultSpecialValue | null }
  >
}

interface IOrderBlueprint {
  orderNumber: string
  caseNumber: string
  /** Milliseconds subtracted from now to produce the sample registration timestamp */
  registrationOffsetMs: number
  samples: ISeedSampleDef[]
  /**
   * Every report sent and awaiting invoicing: labReportStatus 'sent', paymentStatus
   * 'ready-to-charge'. orderStatus stays 'confirmed' — it only reaches 'completed' once
   * paymentStatus is also 'invoice-sent'/'not-required' (see deriveOrderStatuses.md in
   * lab-cloud); no real order can be 'completed' while still 'ready-to-charge'. All
   * samples must use lifecycle 'completed'.
   */
  isReadyToInvoice?: boolean
  /**
   * Pins a delivery (TAT group, keyed by its `tatKey`) to a specific report stage instead of
   * the default — a fully-completed group is otherwise faked straight to 'sent' (see
   * `buildTatGroup`). Lets a blueprint sit a delivery at 'ready', 'approved', 'scheduled',
   * 'sent-manually' or 'sent-failed' (MIL-367). Seeded orders never carry a generated report
   * PDF, so "Send nu" on an 'approved'/'scheduled' one has nothing to send — the stage and
   * the chips are real, the artifact behind them is not. Every sample in a pinned group
   * must be 'completed'.
   */
  deliveryStatuses?: Record<string, TLabReportStatus>
  reportLocale?: string
  /**
   * Billed to the first active insurance customer account instead of the customer's own segment —
   * skipped entirely (see the customer-selection special-case below) if no active
   * insurance customer account exists, or if none of them are linked to any seeded customer account.
   */
  isInsuranceCase?: boolean
  insuranceNumber?: string
  /**
   * Only meaningful when `isInsuranceCase` is true. `'split'` requires
   * `splitPartnerAnalysisNames` — every other analysis on the order defaults to the
   * customer section, matching the real auto-split fallback in
   * `recalculateOrderPricingService.ts`.
   */
  finalInvoicePayer?: TFinalInvoicePayer
  /** Partial Danish analysis names (matched like `analysisNames`) billed to the partner under split. */
  splitPartnerAnalysisNames?: string[]
  /**
   * An insurance customer's own order (MIL-402): owned by the seeded insurance customer and placed
   * by its seeded user (`seedInsuranceCustomerFixtures.ts`), shaped exactly as `createOrderService`
   * forces it — always an insurance case with the insurance customer itself as insurer and payer
   * (`finalInvoicePayer: 'partner'`), an empty `customerAccountID`, priced from its own
   * `InsuranceCustomerAccountPrices`. `isInsuranceCase`/`finalInvoicePayer` are ignored on it.
   * Skipped when a target customer pins the run to one business customer.
   */
  isInsuranceCustomerOwnOrder?: boolean
}

// ────────────────────────────────────────────────────────────────────────────
// Status derivation helpers
// ────────────────────────────────────────────────────────────────────────────

function deriveTestStatus(lifecycle: TSampleLifecycle) {
  if (lifecycle === 'not-registered') return 'awaiting-sample' as const
  if (lifecycle === 'registered') return 'ready-for-processing' as const
  if (lifecycle === 'in-progress') return 'started' as const
  if (lifecycle === 'completed') return 'completed' as const
  if (lifecycle === 'awaiting-qa') return 'awaiting-qa' as const
  return 'cancelled' as const
}

function deriveAnalysisStatus(lifecycle: TSampleLifecycle) {
  if (lifecycle === 'not-registered') return 'awaiting-sample' as const
  if (lifecycle === 'registered') return 'ready-for-processing' as const
  if (lifecycle === 'in-progress') return 'started' as const
  if (lifecycle === 'completed') return 'received' as const
  if (lifecycle === 'awaiting-qa') return 'awaiting-qa' as const
  return 'cancelled' as const
}

function deriveSampleStatus(lifecycle: TSampleLifecycle) {
  if (lifecycle === 'not-registered') return 'not-registered' as const
  if (lifecycle === 'completed') return 'analyses-received' as const
  if (lifecycle === 'cancelled') return 'cancelled' as const
  return 'registered' as const
}

// ────────────────────────────────────────────────────────────────────────────
// Sample builder
// ────────────────────────────────────────────────────────────────────────────

function buildEmbeddedSample(opts: {
  sampleID: string
  sampleNumber: number
  sampleCode: string
  analysisIDs: string[]
  sampleAreaID: string
  tatID: string
  tatGroupID: string
  lifecycle: TSampleLifecycle
  registrationTime: number
  analysisStore: ReturnType<typeof useAnalysisStore>
  analysisTestStore: ReturnType<typeof useAnalysisTestStore>
}): IOrderSample {
  const { lifecycle, registrationTime } = opts
  const isRegistered = lifecycle !== 'not-registered'
  const isStarted =
    lifecycle === 'in-progress' ||
    lifecycle === 'completed' ||
    lifecycle === 'awaiting-qa'
  const isCompleted = lifecycle === 'completed'
  const isAwaitingQa = lifecycle === 'awaiting-qa'
  const isCancelled = lifecycle === 'cancelled'
  const completionTime = registrationTime + 4 * 60 * 60 * 1000
  const cancellationTime = registrationTime + 60 * 60 * 1000
  const qaRequestedTime = registrationTime + 2 * 60 * 60 * 1000

  return {
    sampleID: opts.sampleID,
    sampleNumber: opts.sampleNumber,
    builtYear: 1998,
    tatID: opts.tatID,
    tatGroupID: opts.tatGroupID,
    label: { id: opts.sampleCode, type: 'manual' as const },
    labelPhoto: null,
    analyses: opts.analysisIDs.map((analysisID) => {
      const analysis = opts.analysisStore.allAnalyses.find((x) => x.id === analysisID)
      return {
        analysisID,
        status: deriveAnalysisStatus(lifecycle),
        analysisTests: (analysis?.analysisTestIDs ?? []).map((analysisTestID) => {
          return {
            analysisTestID,
            status: deriveTestStatus(lifecycle),
            timestamps: {
              startedAt: isStarted ? registrationTime : null,
              completedAt: isCompleted ? completionTime : null,
              qaRequestedAt: isAwaitingQa ? qaRequestedTime : null,
              qaApprovedAt: null,
            },
            qaRequestedByUserID: isAwaitingQa ? QA_SEED_PLACEHOLDER_USER_ID : null,
            qaApprovedByUserID: null,
          }
        }),
        timestamps: { cancelledAt: isCancelled ? cancellationTime : null },
      }
    }),
    timestamps: {
      registeredAt: isRegistered ? registrationTime : null,
      lastAnalysisResultReceivedAt: isCompleted ? completionTime : null,
    },
    status: deriveSampleStatus(lifecycle),
    currentLocationID: null,
    forwarding: null,
  }
}

// ────────────────────────────────────────────────────────────────────────────
// AnalysisResult value builder
// ────────────────────────────────────────────────────────────────────────────

/**
 * A plausible result value for a seeded AnalysisResult, matching the given
 * AnalysisTest's type. `isCompleted` picks a real value for a 'completed' sample;
 * an 'awaiting-qa' test instead gets the draft value that triggers its QA — the fixed "Nej"
 * for a prove-based test with `qaTrigger: 'not-proved'`, the first QA answer for a single-select
 * test with `qaTrigger: 'answers'` — which the QA scenario depends on (see the awaiting-qa
 * blueprint comment above).
 *
 * multi-select-text/-number pick real items from `allReportDescriptionItems`
 * (filtered to the test's own `reportDescriptionIDs`) when completed — up to two
 * numeric/text-answered items, plus a third using the test's first active answer
 * override when one is configured (multi-select-number only), so a seeded order
 * exercises the same sort/override rendering a real filled-in result would.
 * Falls back to "nothing to add" when not completed, or when no items are
 * configured/loaded yet to pick from.
 *
 * `valueOverrides` (see `ISeedSampleDef`) lets a 'value-based' test get a specific
 * pinned reading — including 'too-low'/'too-high' — instead of the generic
 * midpoint below. Matched the same way `findAnalysis`/`findArea` match their
 * blueprint keys: partial, case-insensitive, against the test's Danish name.
 */
function buildSeedResultValue(
  analysisTest: AnalysisTest,
  isCompleted: boolean,
  allReportDescriptionItems: ReportDescriptionItem[],
  valueOverrides?: ISeedSampleDef['valueOverrides'],
  isAwaitingQa = false
): TAnalysisResultValue {
  switch (analysisTest.type) {
    case 'prove-based':
      return { type: 'prove-based', isProved: isCompleted ? true : false }
    case 'value-based': {
      const testName = analysisTest.getName('da').toLowerCase()
      const overrideKey = Object.keys(valueOverrides ?? {}).find((key) =>
        testName.includes(key.toLowerCase())
      )
      const override = overrideKey ? valueOverrides?.[overrideKey] : undefined
      return {
        type: 'value-based',
        value: override ? override.value : (analysisTest.minLimit + analysisTest.maxLimit) / 2,
        specialValue: override ? override.specialValue : null,
      }
    }
    case 'single-select': {
      // A sample waiting for QA answers with the answer that asks for it, so "Godkend QA" is there
      // to try right after seeding. Anything else takes the first active answer.
      const qaAnswer = isAwaitingQa
        ? analysisTest.answers.find(
            (x) => x.activeState === 'active' && analysisTest.qaAnswerIDs.includes(x.id)
          )
        : undefined
      const firstActiveAnswer =
        qaAnswer ?? analysisTest.answers.find((x) => x.activeState === 'active')
      return { type: 'single-select', answerID: firstActiveAnswer?.id ?? null }
    }
    case 'multi-select-text': {
      if (!isCompleted) {
        return {
          type: 'multi-select-text',
          reportDescriptionIDs: analysisTest.reportDescriptionIDs,
          nothingToAdd: true,
          items: [],
        }
      }
      const pool = allReportDescriptionItems.filter((x) =>
        analysisTest.reportDescriptionIDs.includes(x.reportDescriptionID)
      )
      const picked = pool.slice(0, 2)
      // Empty when the test's answerMode is 'none' — answerID then stays null
      // for every item, same as a real "no answers" test: the item is
      // selected, nothing to answer it with. Otherwise cycle through the
      // active answers so a seeded 'optional'/'required' test shows more
      // than one answer value rather than repeating the first for every item.
      const activeAnswers = analysisTest.answers.filter(
        (x) => x.activeState === 'active'
      )
      return {
        type: 'multi-select-text',
        reportDescriptionIDs: analysisTest.reportDescriptionIDs,
        nothingToAdd: picked.length === 0,
        items: picked.map((item, i) => ({
          reportDescriptionItemID: item.id,
          answerID: activeAnswers[i % activeAnswers.length]?.id ?? null,
        })),
      }
    }
    case 'multi-select-number': {
      if (!isCompleted) {
        return {
          type: 'multi-select-number',
          reportDescriptionIDs: analysisTest.reportDescriptionIDs,
          nothingToAdd: true,
          items: [],
          sumValue: null,
        }
      }
      const pool = allReportDescriptionItems.filter((x) =>
        analysisTest.reportDescriptionIDs.includes(x.reportDescriptionID)
      )
      const numericValues = [12, 24]
      const numericItems = pool.slice(0, numericValues.length).map((item, i) => ({
        reportDescriptionItemID: item.id,
        value: numericValues[i] ?? 0,
        answerOverrideID: null,
      }))
      const override = analysisTest.answerOverrides.find(
        (x) => x.activeState === 'active'
      )
      const overrideItemPoolEntry = pool[numericItems.length]
      const overrideItems =
        override && overrideItemPoolEntry
          ? [
              {
                reportDescriptionItemID: overrideItemPoolEntry.id,
                value: null,
                answerOverrideID: override.id,
              },
            ]
          : []
      const items = [...numericItems, ...overrideItems]
      const sum = numericItems.reduce((total, item) => total + item.value, 0)
      return {
        type: 'multi-select-number',
        reportDescriptionIDs: analysisTest.reportDescriptionIDs,
        nothingToAdd: items.length === 0,
        items,
        sumValue:
          analysisTest.options.hasSumValue && items.length > 0 ? `${sum} CFU` : null,
      }
    }
  }
}

// ────────────────────────────────────────────────────────────────────────────
// TAT group builder
// ────────────────────────────────────────────────────────────────────────────

function buildTatGroup(
  tatGroupID: string,
  tatID: string,
  lifecycles: TSampleLifecycle[]
): IOrderTatGroup {
  const nonCancelled = lifecycles.filter((l) => l !== 'cancelled')
  const allNotRegistered = lifecycles.every((l) => l === 'not-registered')
  const allCompleted = nonCancelled.length > 0 && nonCancelled.every((l) => l === 'completed')
  const anyCompleted = nonCancelled.some((l) => l === 'completed')

  const sampleRegistrationStatus = allNotRegistered ? 'in-transit' as const : 'handed-over' as const

  const analysesDeliveryStatus = (() => {
    if (allNotRegistered) return 'awaiting-samples' as const
    if (allCompleted) return 'received' as const
    if (anyCompleted) return 'received-partially' as const
    return 'awaiting-processing' as const
  })()

  // A seeded order never generates a real report PDF, so there's no genuine
  // "ready, pending approval" state to seed — every fully-completed group is
  // faked straight to 'sent' so CA's result view (which gates on this field,
  // not the underlying AnalysisResult docs — see OrderResultsView.vue) shows
  // the real seeded results instead of "Afventer resultat" forever.
  const labReportStatus = allCompleted ? 'sent' as const : 'not-ready' as const

  return {
    tatGroupID,
    tatID,
    statuses: { sampleRegistrationStatus, analysesDeliveryStatus, labReportStatus },
    timestamps: {
      labReportApprovedAt: null,
      labReportSentAt: null,
      labReportSentFailedAt: null,
    },
    labReportApprovedByUserID: null,
    labReportPdfs: [],
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Report template assignments + responsible user assignments
// ────────────────────────────────────────────────────────────────────────────

function buildReportTemplateAssignments(
  samples: Array<{ analysisIDs: string[]; tatGroupID: string }>,
  analysisStore: ReturnType<typeof useAnalysisStore>
): TOrderReportTemplateAssignments {
  const assignments: TOrderReportTemplateAssignments = {}
  for (const sample of samples) {
    for (const analysisID of sample.analysisIDs) {
      const key = `${sample.tatGroupID}__${analysisID}`
      if (!(key in assignments)) {
        const analysis = analysisStore.allAnalyses.find((x) => x.id === analysisID)
        assignments[key] = analysis?.labReportTemplateID ?? null
      }
    }
  }
  return assignments
}

function buildReportResponsibleUserAssignments(
  reportTemplateAssignments: TOrderReportTemplateAssignments,
  readyTatGroupIDs: Set<string>,
  userID: string | null,
): Record<string, string | null> {
  const assignments: Record<string, string | null> = {}
  for (const [key, templateID] of Object.entries(reportTemplateAssignments)) {
    if (!templateID) continue
    const tatGroupID = key.split('__')[0] ?? ''
    if (!readyTatGroupIDs.has(tatGroupID)) continue
    const assignmentKey = `${tatGroupID}__${templateID}`
    if (!(assignmentKey in assignments)) {
      assignments[assignmentKey] = userID
    }
  }
  return assignments
}

// ────────────────────────────────────────────────────────────────────────────
// Pricing builder
// ────────────────────────────────────────────────────────────────────────────

interface IResolvedSampleForPricing {
  analysisIDs: string[]
  tatID: string
  lifecycle: TSampleLifecycle
}

/**
 * Same shape for `CustomerAccountPrice` and `InsuranceCustomerAccountPrice` (see `InsuranceCustomerAccountPrice.types.ts`'s
 * re-exports from `CustomerAccountPrice.types.ts`) — one resolver covers whichever party is
 * paying a given line.
 */
type IPartyPriceDoc = Pick<
  ICustomerAccountPrice,
  'pricingSegmentID' | 'currencyCode' | 'analyses' | 'tats' | 'deals'
>

function resolvePartyPricing(
  priceDoc: IPartyPriceDoc | null,
  pricingSegmentStore: ReturnType<typeof usePricingSegmentStore>
): {
  active: ReturnType<typeof mergePrices>['active']
  currency: string
  segmentDeals: TPricingSegmentDealEntry[]
  ownDeals: TCustomerAccountPriceDealEntry[]
} {
  const pricingSegment = priceDoc?.pricingSegmentID
    ? (pricingSegmentStore.allPricingSegments.find(
        (x) => x.id === priceDoc.pricingSegmentID
      ) ?? null)
    : null

  const { active } = mergePrices({
    segmentAnalyses: pricingSegment?.analyses ?? [],
    customerAnalyses: priceDoc?.analyses ?? [],
    segmentTats: pricingSegment?.tats ?? [],
    customerTats: priceDoc?.tats ?? [],
    allAnalyses: [],
    allTats: [],
  })

  const currency: string = priceDoc?.currencyCode ?? pricingSegment?.currencyCode ?? ''

  return {
    active,
    currency,
    segmentDeals: pricingSegment?.deals ?? [],
    ownDeals: priceDoc?.deals ?? [],
  }
}

function buildOrderPricing(
  resolvedSamples: IResolvedSampleForPricing[],
  /**
   * The ordering party's own price document — a business customer's `CustomerAccountPrice`, or
   * for an insurance customer's own order its `InsuranceCustomerAccountPrice` (MIL-402).
   */
  customerPriceDoc: IPartyPriceDoc | null,
  pricingSegmentStore: ReturnType<typeof usePricingSegmentStore>,
  /**
   * Present only for `finalInvoicePayer: 'split'` blueprints — every analysis in
   * `partnerAnalysisIDs` prices from (and is stamped billingTarget: 'partner' against) the
   * partner's own price doc; every other analysis defaults to the customer section, mirroring
   * `resolveAnalysisBillingTarget()`'s real fallback in `recalculateOrderPricingService.ts`.
   */
  splitBilling?: {
    partnerAnalysisIDs: Set<string>
    insuranceCustomerAccountPriceDoc: InsuranceCustomerAccountPrice | null
  }
): { orderLines: IOrderLine[]; orderTotals: IOrderTotals; orderDeals: TOrderDealSnapshot[] } {
  const customerPricing = resolvePartyPricing(customerPriceDoc, pricingSegmentStore)
  const insuranceCustomerAccountPricing = splitBilling
    ? resolvePartyPricing(splitBilling.insuranceCustomerAccountPriceDoc, pricingSegmentStore)
    : null

  const currency: string = customerPricing.currency || insuranceCustomerAccountPricing?.currency || ''

  const uniqueAnalysisIDs = [
    ...new Set(resolvedSamples.flatMap((rs) => rs.analysisIDs)),
  ]

  const orderLines: IOrderLine[] = []
  const orderDeals: TOrderDealSnapshot[] = []

  for (const analysisID of uniqueAnalysisIDs) {
    const nonCancelledForAnalysis = resolvedSamples.filter(
      (rs) => rs.analysisIDs.includes(analysisID) && rs.lifecycle !== 'cancelled'
    )
    const quantity = nonCancelledForAnalysis.length

    const billingTarget: TOrderLineBillingTarget | undefined = splitBilling
      ? splitBilling.partnerAnalysisIDs.has(analysisID)
        ? 'partner'
        : 'customer'
      : undefined
    const partyPricing =
      billingTarget === 'partner' && insuranceCustomerAccountPricing ? insuranceCustomerAccountPricing : customerPricing

    const analysisEntry = partyPricing.active.analyses.find((x) => x.analysisID === analysisID)
    const basePriceValue = analysisEntry?.priceValue ?? null
    // Mirrors createOrderService.ts/recalculateOrderPricingService.ts — resolve the payer's
    // effective deal (own override, else the segment's) and build tiered/interval lines the
    // same way the real order-pricing services do, so seeded deals actually materialize into
    // real order lines and an orderDeals snapshot instead of a flat single-price line.
    const deal = resolveEffectiveDeal(analysisID, partyPricing.segmentDeals, partyPricing.ownDeals)
    const { lines: analysisLines, dealSnapshot } = buildAnalysisOrderLines({
      analysisID,
      quantity,
      basePriceValue,
      deal,
    })
    orderLines.push(
      ...(billingTarget
        ? analysisLines.map((line) => ({ ...line, billingTarget }))
        : analysisLines)
    )
    if (dealSnapshot) {
      orderDeals.push(dealSnapshot)
    }

    // Rush-fee lines — one per unique tatID applied to this analysis
    const tatIDsForAnalysis = [
      ...new Set(nonCancelledForAnalysis.map((rs) => rs.tatID).filter(Boolean)),
    ]
    for (const tatID of tatIDsForAnalysis) {
      const tatEntry = analysisEntry?.tats.find((x) => x.tatID === tatID)
      const tatUnitPrice = tatEntry?.priceValue ?? 0
      const isApplicable = tatEntry?.isActive ?? false
      const tatSampleCount = nonCancelledForAnalysis.filter(
        (rs) => rs.tatID === tatID
      ).length
      if (tatUnitPrice > 0 && isApplicable && tatSampleCount > 0) {
        orderLines.push({
          type: 'rush-fee',
          analysisID,
          tatID,
          quantity: tatSampleCount,
          unitPrice: tatUnitPrice,
          total: tatSampleCount * tatUnitPrice,
          ...(billingTarget ? { billingTarget } : {}),
        })
      }
    }
  }

  const analysesTotals: IAnalysesTotals = {}
  for (const analysisID of uniqueAnalysisIDs) {
    if (!analysesTotals[analysisID]) {
      analysesTotals[analysisID] = { quantity: 0, analyses: 0, rushFee: 0 }
    }
  }
  for (const line of orderLines) {
    if (line.analysisID === undefined) {continue}
    const entry = analysesTotals[line.analysisID]
    if (!entry) {continue}
    if (line.type === 'analysis') {
      // A dealt analysis can have more than one 'analysis' line (base + tier1 +
      // tier2) — accumulate rather than overwrite, matching recalculateOrderPricingService.ts.
      entry.quantity += line.quantity
      entry.analyses += line.total
    } else if (line.type === 'rush-fee') {
      entry.rushFee += line.total
    }
  }

  const subTotal = orderLines.reduce((sum, line) => sum + line.total, 0)
  const orderTotals: IOrderTotals = {
    analyses: analysesTotals,
    subTotal,
    currency,
  }

  return { orderLines, orderTotals, orderDeals }
}

// ────────────────────────────────────────────────────────────────────────────
// Blueprints
// ────────────────────────────────────────────────────────────────────────────

const DAY = 24 * 60 * 60 * 1000

// Consolidated 2026-09-24 from 12 orders down to 10 — several of the original
// twelve were slicing the exact same "order with pending deliveries" state
// three different ways (see DEV-003 below). Ordered as a rough progression
// from the simplest possible order state (DEV-001) through to the deliberately
// maximal one (DEV-010) — see src/dev/seed/README.md for the full reasoning.
// Every blueprint except DEV-010 stays small and single-purpose on principle;
// DEV-010 alone is allowed to be as big as the catalog allows, so there is
// exactly one place to check full test-type/report coverage rather than that
// coverage being smeared thin across several orders.
const BLUEPRINTS: IOrderBlueprint[] = [
  // ── Order 1: Just created ──────────────────────────────────────────────────
  {
    orderNumber: 'DEV-001',
    caseNumber: 'CASE-DEV-001',
    registrationOffsetMs: 0,
    samples: [
      {
        sampleCode: 'DEV-001-S1',
        analysisNames: ['7 metaller', 'pcb'],
        sampleAreaName: 'bygge',
        tatKey: '5d17',
        lifecycle: 'not-registered',
      },
      {
        sampleCode: 'DEV-001-S2',
        analysisNames: ['kulbrinter'],
        sampleAreaName: 'bygge',
        tatKey: '5d17',
        lifecycle: 'not-registered',
      },
    ],
  },

  // ── Order 2: Registered, single delivery — nothing processed yet ──────────
  {
    orderNumber: 'DEV-002',
    caseNumber: 'CASE-DEV-002',
    registrationOffsetMs: 1 * DAY,
    samples: [
      {
        sampleCode: 'DEV-002-S1',
        analysisNames: ['mycometer surface'],
        sampleAreaName: 'skimmel',
        tatKey: '5d17',
        lifecycle: 'registered',
      },
      {
        sampleCode: 'DEV-002-S2',
        analysisNames: ['aftryksplade', 'skimmelsvampeforekomst'],
        sampleAreaName: 'skimmel',
        tatKey: '5d17',
        lifecycle: 'registered',
      },
    ],
  },

  // ── Order 3: 3 deliveries at 3 different stages ───────────────────────────
  // Replaces the old DEV-002/DEV-003/DEV-004 — those three orders each showed
  // one slice of "an order with pending deliveries" (overdue-vs-not-overdue,
  // partial-completion-with-a-cancelled-sample, multi-delivery-at-once). One
  // order now covers the full spread instead of three separate ones:
  //   Delivery A (1d17) — overdue, still awaiting processing (nothing done)
  //   Delivery B (3d17) — not yet due, partially processed + a cancelled sample
  //   Delivery C (7d17) — not yet due, samples not even registered yet
  {
    orderNumber: 'DEV-003',
    caseNumber: 'CASE-DEV-003',
    registrationOffsetMs: 2 * DAY,
    samples: [
      {
        sampleCode: 'DEV-003-S1',
        analysisNames: ['asbest materiale'],
        sampleAreaName: 'asbest',
        tatKey: '1d17',
        lifecycle: 'registered',
      },
      {
        sampleCode: 'DEV-003-S2',
        analysisNames: ['asbest materiale'],
        sampleAreaName: 'asbest',
        tatKey: '1d17',
        lifecycle: 'registered',
      },
      {
        sampleCode: 'DEV-003-S3',
        analysisNames: ['7 metaller'],
        sampleAreaName: 'bygge',
        tatKey: '3d17',
        lifecycle: 'completed',
      },
      {
        sampleCode: 'DEV-003-S4',
        analysisNames: ['klorparaffiner'],
        sampleAreaName: 'bygge',
        tatKey: '3d17',
        lifecycle: 'in-progress',
      },
      {
        sampleCode: 'DEV-003-S5',
        analysisNames: ['asbest luft'],
        sampleAreaName: 'asbest',
        tatKey: '3d17',
        lifecycle: 'completed',
      },
      {
        sampleCode: 'DEV-003-S6',
        analysisNames: ['pcb'],
        sampleAreaName: 'bygge',
        tatKey: '3d17',
        lifecycle: 'cancelled',
      },
      {
        sampleCode: 'DEV-003-S7',
        analysisNames: ['8 metaller'],
        sampleAreaName: 'bygge',
        tatKey: '7d17',
        lifecycle: 'not-registered',
      },
      {
        sampleCode: 'DEV-003-S8',
        analysisNames: ['8 metaller'],
        sampleAreaName: 'bygge',
        tatKey: '7d17',
        lifecycle: 'not-registered',
      },
    ],
  },

  // ── Order 4: All samples cancelled ────────────────────────────────────────
  // Bacteria + tree/insect analyses, unused elsewhere in this file — a cancelled
  // sample never gets an AnalysisResult, so this is a low-cost place to touch
  // test types the rest of the set doesn't reach.
  {
    orderNumber: 'DEV-004',
    caseNumber: 'CASE-DEV-004',
    registrationOffsetMs: 1 * DAY,
    samples: [
      {
        sampleCode: 'DEV-004-S1',
        analysisNames: ['bactiquant'],
        sampleAreaName: 'bakterier',
        tatKey: '3d17',
        lifecycle: 'cancelled',
      },
      {
        sampleCode: 'DEV-004-S2',
        analysisNames: ['e-coli'],
        sampleAreaName: 'bakterier',
        tatKey: '3d17',
        lifecycle: 'cancelled',
      },
      {
        sampleCode: 'DEV-004-S3',
        analysisNames: ['insektbestemmelse'],
        sampleAreaName: 'træ',
        tatKey: '5d17',
        lifecycle: 'cancelled',
      },
      {
        sampleCode: 'DEV-004-S4',
        analysisNames: ['urin'],
        sampleAreaName: 'træ',
        tatKey: '5d17',
        lifecycle: 'cancelled',
      },
    ],
  },

  // ── Order 5: QA approval — a second opinion is pending, for both kinds of test ──
  // S1 "Asbest materiale" is a single-select test (Ja / Ja (forurenet) / Nej) that asks for QA on
  // "Nej"; S2 "Asbest luft" is a prove-based test with `qaTrigger: 'not-proved'`. Each is seeded
  // with its QA-triggering answer ("Nej") — ready to test "Godkend QA" immediately after seeding,
  // and "Anmod om QA" once approved+edited or from a fresh sample (see DEV-002).
  {
    orderNumber: 'DEV-005',
    caseNumber: 'CASE-DEV-005',
    registrationOffsetMs: 1 * DAY,
    samples: [
      {
        sampleCode: 'DEV-005-S1',
        analysisNames: ['asbest materiale'],
        sampleAreaName: 'asbest',
        tatKey: '5d17',
        lifecycle: 'awaiting-qa',
      },
      {
        sampleCode: 'DEV-005-S2',
        analysisNames: ['asbest luft'],
        sampleAreaName: 'asbest',
        tatKey: '5d17',
        lifecycle: 'awaiting-qa',
      },
    ],
  },

  // ── Orders 6-7: Completed with reports sent — ready to invoice ─────────────
  // Two orders sitting at paymentStatus 'ready-to-charge', for testing invoicing flows
  // (the single push) without
  // first driving an order through results entry and report approval by hand. A third
  // "ready to invoice" order isn't needed — crediting is a follow-on action performed
  // after invoicing, not a state that needs its own dedicated pristine seed order.
  // "7 metaller"/"pcb" specifically — not swapped for other analyses here — because
  // `pricing-segments.json`'s base segment prices both with real deals (tiered/interval),
  // and this order is what exercises deal-priced invoicing end to end (see the
  // deal-materialization note above). Keep these two on a ready-to-invoice order.
  {
    orderNumber: 'DEV-006',
    caseNumber: 'CASE-DEV-006',
    registrationOffsetMs: 4 * DAY,
    isReadyToInvoice: true,
    samples: [
      {
        sampleCode: 'DEV-006-S1',
        analysisNames: ['7 metaller'],
        sampleAreaName: 'bygge',
        tatKey: '3d17',
        lifecycle: 'completed',
      },
      {
        sampleCode: 'DEV-006-S2',
        analysisNames: ['pcb'],
        sampleAreaName: 'bygge',
        tatKey: '3d17',
        lifecycle: 'completed',
      },
    ],
  },
  {
    orderNumber: 'DEV-007',
    caseNumber: 'CASE-DEV-007',
    registrationOffsetMs: 3 * DAY,
    isReadyToInvoice: true,
    samples: [
      {
        sampleCode: 'DEV-007-S1',
        analysisNames: ['asbest materiale'],
        sampleAreaName: 'asbest',
        tatKey: '2d17',
        lifecycle: 'completed',
      },
      {
        sampleCode: 'DEV-007-S2',
        analysisNames: ['asbest materiale'],
        sampleAreaName: 'asbest',
        tatKey: '2d17',
        lifecycle: 'completed',
      },
      {
        sampleCode: 'DEV-007-S3',
        analysisNames: ['asbest materiale'],
        sampleAreaName: 'asbest',
        tatKey: '2d17',
        lifecycle: 'completed',
      },
    ],
  },

  // ── Order 8: Insurance case, billed to an insurance customer account ─────────────────────────
  {
    orderNumber: 'DEV-008',
    caseNumber: 'CASE-DEV-008',
    registrationOffsetMs: 0,
    isInsuranceCase: true,
    insuranceNumber: 'SKADE-DEV-008',
    samples: [
      {
        sampleCode: 'DEV-008-S1',
        analysisNames: ['asbest materiale'],
        sampleAreaName: 'asbest',
        tatKey: '2d17',
        lifecycle: 'not-registered',
      },
    ],
  },

  // ── Order 9: Insurance case, split between customer and partner, ready to invoice ──
  // Worked example for the Final Invoice Payer plan (split mode) — see
  // src/dev/seed/README.md. "7 metaller" bills the partner (priced at the partner's own
  // segment), "asbest materiale" defaults to the customer section — two fully-priced
  // sections, ready to send, so the split UI has something real to render out of the box.
  {
    orderNumber: 'DEV-009',
    caseNumber: 'CASE-DEV-009',
    registrationOffsetMs: 1 * DAY,
    isInsuranceCase: true,
    insuranceNumber: 'SKADE-DEV-009',
    finalInvoicePayer: 'split',
    splitPartnerAnalysisNames: ['7 metaller'],
    isReadyToInvoice: true,
    samples: [
      {
        sampleCode: 'DEV-009-S1',
        analysisNames: ['asbest materiale'],
        sampleAreaName: 'asbest',
        tatKey: '2d17',
        lifecycle: 'completed',
      },
      {
        sampleCode: 'DEV-009-S2',
        analysisNames: ['7 metaller'],
        sampleAreaName: 'bygge',
        tatKey: '3d17',
        lifecycle: 'completed',
      },
    ],
  },

  // ── Order 10: One of each of the 5 AnalysisTest types, all completed with
  // real results, report(s) marked sent — a single stop to check every test
  // type's report rendering at once (MIL-348 follow-up). Also covers all
  // three multi-select-text answerModes:
  //
  //   prove-based          → Asbest geltape → Asbest generel (asks for QA on "Nej"; the seeded result is "Ja")
  //   single-select        → Asbest geltape → Fibertype
  //   value-based          → 7 Metaller / PCB (S4, see below)
  //   multi-select-number  → Aftryksplade → Levedygtige svampesporer
  //   multi-select-text    → Svampe og råd → Nedbrydning / Yderligere / Karakteristik
  //     (answerMode 'none' — item selected, no answer to give it)
  //   multi-select-text    → Skimmelsvampeforekomst (answerMode 'optional')
  //   multi-select-text    → Insektforekomst (answerMode 'required')
  //
  // S4 additionally combines 7 Metaller + PCB on one sample (value-based, via
  // valueOverrides) so a completed order also exercises the 'too-low'/'too-high'
  // special-value states, not just plain numeric readings.
  //
  // Deliberately does NOT include Mycometer Surface, even though it's the more
  // obvious value-based pick for this sample area — the catalog leaves it
  // without a base price on purpose (see "Deliberate gaps" in this file's
  // README), so including it here would put this order — meant to be the
  // clean, fully-priced, fully-complete showcase — into the "Mangler priser"
  // state too. Confirmed 2026-09-24: it was here originally and did exactly
  // that. DEV-002 already carries Mycometer Surface for that gap's own sake.
  //
  // The deliberately maximal blueprint in this set — see the file-level comment
  // above BLUEPRINTS for why everything else stays small and single-purpose.
  //
  // All samples share one TAT key, so they land in a single TAT group — but
  // still split into three report groups by template (rt-asbest, rt-svamp-og-raad,
  // rt-lab), since "Asbest geltape" and "Svampe og råd" each use their own report
  // template. rt-svamp-og-raad is the per-sample ('samples-explained') setup.
  {
    orderNumber: 'DEV-010',
    caseNumber: 'CASE-DEV-010',
    registrationOffsetMs: 1 * DAY,
    samples: [
      {
        sampleCode: 'DEV-010-S1',
        analysisNames: ['asbest geltape'],
        sampleAreaName: 'asbest',
        tatKey: '5d17',
        lifecycle: 'completed',
      },
      {
        sampleCode: 'DEV-010-S2',
        analysisNames: ['aftryksplade', 'skimmelsvampeforekomst'],
        sampleAreaName: 'skimmel',
        tatKey: '5d17',
        lifecycle: 'completed',
      },
      {
        sampleCode: 'DEV-010-S3',
        analysisNames: ['svampe og råd', 'insektforekomst'],
        sampleAreaName: 'træ',
        tatKey: '5d17',
        lifecycle: 'completed',
      },
      // 7 Metaller + PCB together, all value-based, fully filled and completed —
      // the metals cycle normal / too-low / too-high so a single sample exercises
      // all three reading states at once; PCB gets one plain number.
      {
        sampleCode: 'DEV-010-S4',
        analysisNames: ['7 metaller', 'pcb'],
        sampleAreaName: 'bygge',
        tatKey: '5d17',
        lifecycle: 'completed',
        valueOverrides: {
          bly: { value: 20, specialValue: null },
          kobber: { value: null, specialValue: 'too-low' },
          nikkel: { value: null, specialValue: 'too-high' },
          zink: { value: 300, specialValue: null },
          kviksølv: { value: null, specialValue: 'too-low' },
          cadmium: { value: null, specialValue: 'too-high' },
          chrom: { value: 250, specialValue: null },
          pcb: { value: 5, specialValue: null },
        },
      },
    ],
  },
  // ── Orders 11–16: one delivery stage per order (MIL-367) ──────────────────
  // Every report stage a delivery can sit at, one order each, so each shows as its own row
  // in the deliveries list and on its own Leveringer tab. Seeded orders carry no generated report PDF — see
  // `deliveryStatuses` on `IOrderBlueprint`.

  // ── Order 11: Report ready — waiting for approval ─────────────────────────────────
  // Approve it from the Leveringer tab to try the three options.
  {
    orderNumber: 'DEV-011',
    caseNumber: 'CASE-DEV-011',
    registrationOffsetMs: 3 * DAY,
    deliveryStatuses: { '5d17': 'ready' },
    samples: [
      {
        sampleCode: 'DEV-011-S1',
        analysisNames: ['asbest luft'],
        sampleAreaName: 'asbest',
        tatKey: '5d17',
        lifecycle: 'completed',
      },
    ],
  },

  // ── Order 12: Report approved, not sent ─────────────────────────────────
  // Parked on purpose — shows "Send nu" and "Generer og marker som manuelt sendt".
  {
    orderNumber: 'DEV-012',
    caseNumber: 'CASE-DEV-012',
    registrationOffsetMs: 3 * DAY,
    deliveryStatuses: { '5d17': 'approved' },
    samples: [
      {
        sampleCode: 'DEV-012-S1',
        analysisNames: ['kulbrinter'],
        sampleAreaName: 'bygge',
        tatKey: '5d17',
        lifecycle: 'completed',
      },
    ],
  },

  // ── Order 13: Report scheduled for the send day ─────────────────────────────────
  // Shows the 08:00 send day; "Annullér planlægning" brings it back to approved.
  {
    orderNumber: 'DEV-013',
    caseNumber: 'CASE-DEV-013',
    registrationOffsetMs: 3 * DAY,
    deliveryStatuses: { '5d17': 'scheduled' },
    samples: [
      {
        sampleCode: 'DEV-013-S1',
        analysisNames: ['klorparaffiner'],
        sampleAreaName: 'bygge',
        tatKey: '5d17',
        lifecycle: 'completed',
      },
    ],
  },

  // ── Order 14: Report marked as sent manually ─────────────────────────────────
  // Delivered outside the system, so the order is ready to invoice.
  {
    orderNumber: 'DEV-014',
    caseNumber: 'CASE-DEV-014',
    registrationOffsetMs: 3 * DAY,
    deliveryStatuses: { '5d17': 'sent-manually' },
    samples: [
      {
        sampleCode: 'DEV-014-S1',
        analysisNames: ['8 metaller'],
        sampleAreaName: 'bygge',
        tatKey: '5d17',
        lifecycle: 'completed',
      },
    ],
  },

  // ── Order 15: Report send failed ─────────────────────────────────
  // Shows "Prøv igen" on the delivery.
  {
    orderNumber: 'DEV-015',
    caseNumber: 'CASE-DEV-015',
    registrationOffsetMs: 3 * DAY,
    deliveryStatuses: { '5d17': 'sent-failed' },
    samples: [
      {
        sampleCode: 'DEV-015-S1',
        analysisNames: ['asbest materiale'],
        sampleAreaName: 'asbest',
        tatKey: '5d17',
        lifecycle: 'completed',
      },
    ],
  },

  // ── Order 16: three deliveries — one delivered, one parked, one still in progress ──
  // The mixed case for the order list chip: still a plain "1/3 leveringer" while one delivery is in progress.
  {
    orderNumber: 'DEV-016',
    caseNumber: 'CASE-DEV-016',
    registrationOffsetMs: 3 * DAY,
    deliveryStatuses: { '1d17': 'sent', '3d17': 'approved' },
    samples: [
      {
        sampleCode: 'DEV-016-S1',
        analysisNames: ['asbest luft'],
        sampleAreaName: 'asbest',
        tatKey: '1d17',
        lifecycle: 'completed',
      },
      {
        sampleCode: 'DEV-016-S2',
        analysisNames: ['kulbrinter'],
        sampleAreaName: 'bygge',
        tatKey: '3d17',
        lifecycle: 'completed',
      },
      {
        sampleCode: 'DEV-016-S3',
        analysisNames: ['klorparaffiner'],
        sampleAreaName: 'bygge',
        tatKey: '7d17',
        lifecycle: 'in-progress',
      },
    ],
  },

  // ── Order 17: every multi-select layout, filled in but not finished ──────────
  // The "Multivalg: alle varianter" analysis (MIL-428) with a saved draft value on each of
  // its 15 tests — what "Gem" leaves behind. Nothing is completed, approved or sent, so
  // open the analysis and edit what is already there (MIL-429).
  {
    orderNumber: 'DEV-017',
    caseNumber: 'CASE-DEV-017',
    registrationOffsetMs: 2 * DAY,
    samples: [
      {
        sampleCode: 'DEV-017-S1',
        analysisNames: ['multivalg'],
        sampleAreaName: 'skimmel',
        tatKey: '7d17',
        lifecycle: 'in-progress',
        hasDraftResults: true,
      },
    ],
  },

  // ── Order 18: an insurance customer's own order (MIL-402) ───────────────────
  // Owned by the seeded insurance customer and placed by its seeded user — always an insurance
  // case with itself as insurer and payer. It carries an insurance number because that is mandatory
  // for these orders (since 2026-10-23) — without one it would be held at invoicing. Ready to
  // invoice, so the 'partner' payer path into BC has something real to push.
  {
    orderNumber: 'DEV-018',
    caseNumber: 'CASE-DEV-018',
    insuranceNumber: 'SKADE-DEV-018',
    registrationOffsetMs: 3 * DAY,
    isInsuranceCustomerOwnOrder: true,
    isReadyToInvoice: true,
    samples: [
      {
        sampleCode: 'DEV-018-S1',
        analysisNames: ['asbest materiale'],
        sampleAreaName: 'asbest',
        tatKey: '2d17',
        lifecycle: 'completed',
      },
    ],
  },

]

export const SEED_ORDER_COUNT = BLUEPRINTS.length

// ────────────────────────────────────────────────────────────────────────────
// Main
// ────────────────────────────────────────────────────────────────────────────

export async function runSeedOrders(
  accountID: string,
  userID: string,
  targetCustomerAccountID?: string,
  targetCustomerUserID?: string
): Promise<void> {
  const analysisStore = useAnalysisStore()
  const analysisTestStore = useAnalysisTestStore()
  const sampleAreaStore = useSampleAreaStore()
  const turnaroundTimeStore = useTurnaroundTimeStore()
  const customerAccountStore = useCustomerAccountStore()
  const customerUserStore = useCustomerUserStore()
  const dropPointStore = useDropPointStore()
  const accountStore = useAccountStore()
  const pricingSegmentStore = usePricingSegmentStore()
  const reportDescriptionItemStore = useReportDescriptionItemStore()
  const textListStore = useTextListStore()

  const allReportDescriptionItems = (
    await reportDescriptionItemStore.getReportDescriptionItems()
  ).filter((x) => x.activeState === 'active')

  // Unit labels for the sum of a filled multi-select-number draft, resolved in Danish.
  const resultUnits = (await textListStore.getTextLists()).find(
    (x) => x.type === 'system' && 'systemKey' in x && x.systemKey === 'result-units'
  )
  const unitLabelFor = (unitID: string): string => {
    const text = resultUnits && 'items' in resultUnits ? resultUnits.items[unitID]?.text : undefined
    return text?.['da'] ?? Object.values(text ?? {}).find((v) => v !== '') ?? ''
  }

  const analyses = analysisStore.allAnalyses.filter((x) => x.activeState === 'active')
  const sampleAreas = sampleAreaStore.allSampleAreas.filter((x) => x.activeState === 'active')
  const tats = turnaroundTimeStore.allTATs
  const customers = customerAccountStore.allCustomerAccounts.filter(
    (x) => x.activeState === 'active'
  )
  // Business users only: every blueprint but the insurance-owned one is a business order, and an
  // insurance or private user must never end up as its ordering user (MIL-402).
  const customerUsers = customerUserStore.customerUsersBusiness.filter(
    (x) => x.activeState === 'active'
  )

  if (!analyses.length || !sampleAreas.length || !tats.length) {
    throw new Error('[runSeedOrders] Catalog not seeded — cannot create orders')
  }
  if (!customers.length || !customerUsers.length) {
    throw new Error(
      '[runSeedOrders] No active customers/users — cannot create orders'
    )
  }

  const findAnalysis = (name: string) =>
    analyses.find((x) => x.getName('da').toLowerCase().includes(name.toLowerCase()))

  const findArea = (name: string) =>
    sampleAreas.find((x) => x.getName('da').toLowerCase().includes(name.toLowerCase()))

  const findTat = (tatKey: string) => {
    const fixedMatch = tatKey.match(/^(\d+)d(\d+)$/)
    if (fixedMatch) {
      const workingDays = parseInt(fixedMatch[1]!)
      const hour = parseInt(fixedMatch[2]!)
      return tats.find(
        (x) =>
          x.type === 'fixed-deadline' &&
          x.workingDays === workingDays &&
          x.deadline.hour === hour
      )
    }
    return tats.find(
      (x) =>
        x.type === 'walk-in' &&
        x.getName('da').toLowerCase().includes(tatKey.toLowerCase())
    )
  }

  const firstLabDropPoint =
    dropPointStore.labDropPoints.find((x) => x.activeState === 'active') ?? null
  const firstLabUser =
    accountStore.allLabAccountUsers.find((x) => x.activeState === 'active') ?? null

  // The first active insurance customer alphabetically. Unlike runSeedCustomerPricing.ts, a lab
  // with none gets one created by ensureInsuranceCustomerFixtures below (MIL-402). An
  // isInsuranceCase blueprint is still skipped when that insurance customer is linked to none of
  // the seeded customer accounts.
  const insuranceCustomerAccountStore = useInsuranceCustomerAccountStore()
  const firstActiveInsuranceCustomerAccount =
    [...insuranceCustomerAccountStore.allInsuranceCustomerAccounts]
      .filter((x) => x.activeState === 'active')
      .sort((a, b) => a.name.localeCompare(b.name))[0] ?? null
  // The insurance customer's own user and private drop point (MIL-402) — ensured on every run, and
  // the insurance customer itself created when the lab has none, linked to the first business
  // customer so the insurance-case blueprints below find it too.
  const firstPricedSegment =
    [...pricingSegmentStore.allPricingSegments]
      .filter((x) => x.activeState === 'active' && x.analyses.length > 0 && x.tats.length > 0)
      .sort((a, b) => a.id.localeCompare(b.id))[0] ?? null
  const insuranceFixtures: ISeedInsuranceCustomerFixtures = await ensureInsuranceCustomerFixtures({
    labAccountID: accountID,
    userID,
    existing: firstActiveInsuranceCustomerAccount,
    linkToCustomerAccountID: customers[0]?.id ?? null,
    pricingSegment: firstPricedSegment
      ? { id: firstPricedSegment.id, currencyCode: firstPricedSegment.currencyCode }
      : null,
    defaultLocale:
      LAB_CUSTOMER_LANGUAGES[
        accountStore.labAccount?.defaultCustomerLanguageCode ?? 'EN'
      ]?.locale ?? 'en',
  })
  const insuranceCustomerAccount = insuranceFixtures.insuranceCustomerAccount
  const insuranceCaseCustomerAccount =
    customers.find((x) => insuranceCustomerAccount.customerAccountIDs.includes(x.id)) ?? null
  // The insurance customer's own price document. A freshly created one is not in the store yet, so
  // it is rebuilt from the segment the fixtures pointed it at.
  const insuranceCustomerAccountPriceDoc: IPartyPriceDoc | null =
    insuranceCustomerAccountStore.allInsuranceCustomerAccountPrices.find(
      (x) => x.insuranceCustomerAccountID === insuranceCustomerAccount.id
    ) ??
    (insuranceFixtures.createdInsuranceCustomerAccount && firstPricedSegment
      ? {
          pricingSegmentID: firstPricedSegment.id,
          currencyCode: firstPricedSegment.currencyCode,
          analyses: [],
          tats: [],
          deals: [],
        }
      : null)

  const now = Date.now()
  const batch = writeBatch(db)

  /** Who an order belongs to and who placed it — a business customer, or an insurance customer itself. */
  type TSeedOrderOwner = {
    customer: ICustomerRef
    /** Deprecated (MIL-505), still written: the business customer's ID, `''` for an insurance customer. */
    customerAccountID: string
    customerUser: { id: string; email: string }
    priceDoc: IPartyPriceDoc | null
  }

  BLUEPRINTS.forEach((blueprint, i) => {
    let owner: TSeedOrderOwner
    if (blueprint.isInsuranceCustomerOwnOrder) {
      // A target customer pins the run to one business customer; an insurance-owned order has no
      // place in that.
      if (targetCustomerAccountID) return
      owner = {
        customer: insuranceFixtures.customer,
        customerAccountID: '',
        customerUser: insuranceFixtures.user,
        priceDoc: insuranceCustomerAccountPriceDoc,
      }
    } else {
      // A target customer pins every seeded order to it — including insurance-case
      // blueprints, which otherwise force the insurer-linked account instead.
      let customerAccount = targetCustomerAccountID
        ? customers.find((x) => x.id === targetCustomerAccountID)
        : customers[i % customers.length]
      if (blueprint.isInsuranceCase && !targetCustomerAccountID) {
        // No active insurance customer account linked to any seeded customer — skip gracefully
        // rather than seeding a fake/incorrect insurance-billing relationship.
        if (!insuranceCaseCustomerAccount) return
        customerAccount = insuranceCaseCustomerAccount
      }
      if (!customerAccount) return
      const businessCustomerAccountID = customerAccount.id
      // `customerUsers` holds business users only, so neither the account's own user nor the
      // fallback can be an insurance or private user.
      const customerUser = targetCustomerUserID
        ? customerUsers.find((x) => x.id === targetCustomerUserID)
        : (customerUsers.find(
            (x) => x.customer.type === 'business' && x.customer.id === businessCustomerAccountID
          ) ?? customerUsers[i % customerUsers.length])
      if (!customerUser) return
      owner = {
        customer: { type: 'business', id: businessCustomerAccountID },
        customerAccountID: businessCustomerAccountID,
        customerUser,
        priceDoc:
          customerAccountStore.allCustomerAccountPrices.find(
            (x) => x.customerAccountID === businessCustomerAccountID
          ) ?? null,
      }
    }
    const isInsuranceOwned = blueprint.isInsuranceCustomerOwnOrder === true
    const isInsuranceCase = isInsuranceOwned || (blueprint.isInsuranceCase ?? false)
    const finalInvoicePayer: TFinalInvoicePayer | null = isInsuranceOwned
      ? 'partner'
      : (blueprint.finalInvoicePayer ?? null)
    const insurancePartnerID = isInsuranceOwned
      ? insuranceCustomerAccount.id
      : blueprint.isInsuranceCase
        ? insuranceCustomerAccount.id
        : null

    const registrationTime = now - blueprint.registrationOffsetMs
    const orderCreatedAt = registrationTime - 60 * 60 * 1000

    // ── Resolve unique TAT keys → group IDs + TAT IDs ───────────────────────
    const tatKeyToGroupID = new Map<string, string>()
    const tatKeyToTatID = new Map<string, string>()
    for (const def of blueprint.samples) {
      if (!tatKeyToGroupID.has(def.tatKey)) {
        const tat = findTat(def.tatKey)
        if (!tat) continue
        tatKeyToGroupID.set(def.tatKey, generateFirestoreId())
        tatKeyToTatID.set(def.tatKey, tat.id)
      }
    }

    // ── Resolve each sample def to Firestore refs + IDs ─────────────────────
    type TResolvedSample = {
      sampleRef: ReturnType<typeof doc>
      sampleID: string
      sampleNumber: number
      def: ISeedSampleDef
      analysisIDs: string[]
      areaID: string
      tatID: string
      tatGroupID: string
    }

    const resolved: TResolvedSample[] = []
    blueprint.samples.forEach((def, j) => {
      const tatGroupID = tatKeyToGroupID.get(def.tatKey)
      const tatID = tatKeyToTatID.get(def.tatKey)
      if (!tatGroupID || !tatID) return

      const area = findArea(def.sampleAreaName)
      if (!area) return

      const analysisIDs = def.analysisNames
        .map((name) => findAnalysis(name))
        .filter(Boolean)
        .map((a) => a!.id)
      if (!analysisIDs.length) return

      const sampleRef = doc(collection(db, 'Apps', '-lab-customer-', 'Samples'))
      resolved.push({
        sampleRef,
        sampleID: sampleRef.id,
        sampleNumber: j + 1,
        def,
        analysisIDs,
        areaID: area.id,
        tatID,
        tatGroupID,
      })
    })

    if (!resolved.length) return

    // Created up front (rather than just before the order write, further down)
    // so its ID is available for the AnalysisResults written below.
    const orderRef = doc(collection(db, 'Apps', '-lab-customer-', 'Orders'))

    // ── Seed real AnalysisResults for 'completed' and 'awaiting-qa' tests ─────
    // The embedded order/sample statuses alone aren't enough — both the results-entry
    // modal and the report PDF generator (generateReportPdfsForTatGroup) load actual
    // AnalysisResult docs, not just the embedded status. Without one, a 'completed'
    // test shows as done but has no value to render in the report, and an
    // 'awaiting-qa' prove-based test would render unselected despite the analysis
    // showing "awaiting-qa". Same completionTime formula as buildEmbeddedSample uses below —
    // named differently here since that one is declared later in this same scope.
    const resultCompletionTime = registrationTime + 4 * 60 * 60 * 1000
    for (const rs of resolved) {
      const isCompleted = rs.def.lifecycle === 'completed'
      const isAwaitingQa = rs.def.lifecycle === 'awaiting-qa'
      const isFilledDraft =
        rs.def.lifecycle === 'in-progress' && (rs.def.hasDraftResults ?? false)
      if (!isCompleted && !isAwaitingQa && !isFilledDraft) continue
      for (const analysisID of rs.analysisIDs) {
        const analysis = analysisStore.allAnalyses.find((x) => x.id === analysisID)
        for (const analysisTestID of analysis?.analysisTestIDs ?? []) {
          const analysisTest = analysisTestStore.allAnalysisTests.find(
            (x) => x.id === analysisTestID
          )
          if (!analysisTest) continue
          // The QA flow applies to prove-based tests and to single-select tests that ask for it
          // (`qaTrigger: 'answers'`) — other test types under an 'awaiting-qa' sample are left
          // without a result.
          const isQaCapable =
            analysisTest.type === 'prove-based' ||
            (analysisTest.type === 'single-select' && analysisTest.qaTrigger === 'answers')
          if (isAwaitingQa && !isQaCapable) continue

          const resultRef = doc(collection(db, 'Apps', '-lab-customer-', 'AnalysisResults'))
          batch.set(resultRef, {
            id: resultRef.id,
            orderID: orderRef.id,
            sampleID: rs.sampleID,
            analysisID,
            analysisTestID,
            labAccountID: accountID,
            createdByUserID: userID,
            lastEditedByUserID: userID,
            activeState: 'active' as const,
            status: isCompleted ? ('completed' as const) : ('draft' as const),
            result:
              (isFilledDraft
                ? buildFilledMultiSelectValue(
                    analysisTest,
                    allReportDescriptionItems,
                    'unitID' in analysisTest ? unitLabelFor(analysisTest.unitID) : ''
                  )
                : null) ??
              buildSeedResultValue(
                analysisTest,
                isCompleted || isFilledDraft,
                allReportDescriptionItems,
                rs.def.valueOverrides,
                isAwaitingQa
              ),
            timestamps: {
              createdAt: registrationTime,
              updatedAt: registrationTime,
              completedAt: isCompleted ? resultCompletionTime : null,
            },
          })
        }
      }
    }

    // ── Build embedded samples ───────────────────────────────────────────────
    const embeddedSamples: IOrderSample[] = resolved.map((rs) =>
      buildEmbeddedSample({
        sampleID: rs.sampleID,
        sampleNumber: rs.sampleNumber,
        sampleCode: rs.def.sampleCode,
        analysisIDs: rs.analysisIDs,
        sampleAreaID: rs.areaID,
        tatID: rs.tatID,
        tatGroupID: rs.tatGroupID,
        lifecycle: rs.def.lifecycle,
        registrationTime,
        analysisStore,
        analysisTestStore,
      })
    )

    // ── Build TAT groups ─────────────────────────────────────────────────────
    const tatGroups: Record<string, IOrderTatGroup> = {}
    for (const [tatKey, tatGroupID] of tatKeyToGroupID) {
      const tatID = tatKeyToTatID.get(tatKey)!
      const lifecycles = resolved
        .filter((rs) => rs.tatGroupID === tatGroupID)
        .map((rs) => rs.def.lifecycle)
      tatGroups[tatGroupID] = buildTatGroup(tatGroupID, tatID, lifecycles)
    }

    // Ready-to-invoice orders have every report approved and sent — the state an order is in
    // when it sits at 'ready-to-charge' awaiting invoicing. (In practice every one of their
    // samples is already 'completed', so buildTatGroup would set 'sent' on its own — this
    // stays as an explicit force in case that ever isn't true.)
    const reportSentTime = registrationTime + 6 * 60 * 60 * 1000
    if (blueprint.isReadyToInvoice) {
      for (const group of Object.values(tatGroups)) {
        group.statuses.labReportStatus = 'sent'
      }
    }
    for (const [tatKey, status] of Object.entries(blueprint.deliveryStatuses ?? {})) {
      const group = tatGroups[tatKeyToGroupID.get(tatKey) ?? '']
      if (group) group.statuses.labReportStatus = status
    }
    // Every group past 'ready' gets the timestamps and approver a real one has at that stage —
    // 'sent' via isReadyToInvoice above or because buildTatGroup found every sample 'completed',
    // the rest via `deliveryStatuses`.
    const scheduledForTime = (() => {
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      tomorrow.setHours(8, 0, 0, 0)
      return tomorrow.getTime()
    })()
    for (const group of Object.values(tatGroups)) {
      const status = group.statuses.labReportStatus
      if (status === 'not-ready' || status === 'ready') continue
      group.timestamps.labReportApprovedAt = reportSentTime
      group.labReportApprovedByUserID = firstLabUser?.id ?? null
      if (status === 'sent') group.timestamps.labReportSentAt = reportSentTime
      if (status === 'scheduled') group.timestamps.labReportScheduledForAt = scheduledForTime
      if (status === 'sent-manually') {
        group.timestamps.labReportSentManuallyAt = reportSentTime
        group.labReportSentManuallyByUserID = firstLabUser?.id ?? null
      }
      if (status === 'sent-failed') group.timestamps.labReportSentFailedAt = reportSentTime
    }

    // ── Aggregate order-level statuses ───────────────────────────────────────
    // Mirrors lab-cloud's deriveOrderLevelStatuses() (deriveOrderStatuses.ts) — a real order
    // derives these top-level fields from its TAT groups' own state rather than tracking them
    // independently. The seed script has to do the same, since lab-customer's order-detail
    // header chip (getRealOrderStatusChip) reads only these order-level fields, never
    // tatGroups directly — a seeded order whose groups are all 'sent' but whose order-level
    // labReportStatus/paymentStatus weren't updated to match still shows a stale "awaiting
    // analyses" chip even though every result is real and ready.
    const allSamplesCancelled = resolved.every((rs) => rs.def.lifecycle === 'cancelled')
    const tatGroupList = Object.values(tatGroups)
    const delivStatuses = tatGroupList.map((g) => g.statuses.analysesDeliveryStatus)
    const allHandedOver = tatGroupList.every(
      (g) => g.statuses.sampleRegistrationStatus === 'handed-over'
    )

    const orderSampleRegistrationStatus = allHandedOver
      ? ('handed-over' as const)
      : ('in-transit' as const)

    const orderAnalysesDeliveryStatus = (() => {
      if (delivStatuses.every((s) => s === 'not-required')) return 'not-required' as const
      if (delivStatuses.every((s) => s === 'awaiting-samples')) return 'awaiting-samples' as const
      if (delivStatuses.every((s) => s === 'received' || s === 'not-required'))
        return 'received' as const
      if (delivStatuses.some((s) => s === 'received' || s === 'received-partially'))
        return 'received-partially' as const
      return 'awaiting-processing' as const
    })()

    const orderLabReportStatus: TOrderLabReportStatus = (() => {
      // Same order as lab-cloud's aggregateLabReportStatus: a failure first, then delivered
      // ('sent' or 'sent-manually') — parked ('approved'/'scheduled') counts as not sent.
      const values = tatGroupList.map((g) => g.statuses.labReportStatus)
      if (values.some((s) => s === 'sent-failed')) return 'failed'
      if (values.length > 0 && values.every(isDeliveryDelivered)) return 'sent'
      if (values.some(isDeliveryDelivered)) return 'partially-sent'
      return 'not-sent'
    })()

    // Auto-advance 'awaiting-fulfillment' → 'ready-to-charge' once every report is sent — same
    // rule the real derivation applies. isReadyToInvoice blueprints already start at
    // 'ready-to-charge', so this is a no-op for them.
    const basePaymentStatus: TPaymentStatus = blueprint.isReadyToInvoice
      ? 'ready-to-charge'
      : 'awaiting-fulfillment'
    const orderPaymentStatus: TPaymentStatus =
      orderLabReportStatus === 'sent' && basePaymentStatus === 'awaiting-fulfillment'
        ? 'ready-to-charge'
        : basePaymentStatus

    // No blueprint here ever reaches paymentStatus 'not-required'/'invoice-sent' — the real
    // derivation's 'reports sent + already invoiced' → 'completed' promotion never applies, so
    // orderStatus only ever resolves to one of these two.
    const orderStatus: TOrderStatus = allSamplesCancelled ? 'cancelled' : 'confirmed'

    const dropPointSnapshot: IOrderDropPointSnapshot | null = firstLabDropPoint
      ? {
          id: firstLabDropPoint.id,
          type: firstLabDropPoint.type,
          title:
            firstLabDropPoint.name['da'] ??
            firstLabDropPoint.name['en'] ??
            Object.values(firstLabDropPoint.name).find(Boolean) ??
            '',
          address: {
            addressLine: firstLabDropPoint.address.address,
            postalCode: firstLabDropPoint.address.postalCode,
            city: firstLabDropPoint.address.city,
          },
          placement:
            firstLabDropPoint.placement['da'] ??
            firstLabDropPoint.placement['en'] ??
            Object.values(firstLabDropPoint.placement).find(Boolean) ??
            '',
        }
      : null

    const dropOffDate = new Date(registrationTime).toISOString().split('T')[0] ?? ''
    const sampleHandOver: IOrderSampleHandOver = {
      method: 'drop-point',
      dropPoint: dropPointSnapshot,
      dropoff: { date: dropOffDate, hours: 8, minutes: 0 },
      pickup: null,
    }

    const hasRegistered = resolved.some((rs) => rs.def.lifecycle !== 'not-registered')
    const hasCompleted = resolved.some((rs) => rs.def.lifecycle === 'completed')
    const completionTime = registrationTime + 4 * 60 * 60 * 1000
    const cancellationTime = registrationTime + 60 * 60 * 1000

    // ── Build report assignments ─────────────────────────────────────────────
    const reportTemplateAssignments = buildReportTemplateAssignments(resolved, analysisStore)
    // A report has a responsible user from the moment its delivery is 'ready' to approve —
    // approving needs one — so every group past 'not-ready' gets one, derived straight from
    // each group's final status rather than re-deriving "which groups are ready" via the blueprint.
    const readyTatGroupIDs = new Set(
      Object.entries(tatGroups)
        .filter(([, group]) => group.statuses.labReportStatus !== 'not-ready')
        .map(([tatGroupID]) => tatGroupID)
    )
    const reportResponsibleUserAssignments = buildReportResponsibleUserAssignments(
      reportTemplateAssignments,
      readyTatGroupIDs,
      firstLabUser?.id ?? null,
    )

    // ── Compute order pricing ────────────────────────────────────────────────
    const resolvedForPricing: IResolvedSampleForPricing[] = resolved.map((rs) => ({
      analysisIDs: rs.analysisIDs,
      tatID: rs.tatID,
      lifecycle: rs.def.lifecycle,
    }))
    const splitPartnerAnalysisIDs =
      blueprint.finalInvoicePayer === 'split'
        ? new Set(
            (blueprint.splitPartnerAnalysisNames ?? [])
              .map((name) => findAnalysis(name)?.id)
              .filter((id): id is string => id !== undefined)
          )
        : null
    const insuranceCustomerAccountPriceDocForSplit =
      splitPartnerAnalysisIDs
        ? (insuranceCustomerAccountStore.allInsuranceCustomerAccountPrices.find(
            (x) => x.insuranceCustomerAccountID === insuranceCustomerAccount.id
          ) ?? null)
        : null
    const { orderLines, orderTotals, orderDeals } = buildOrderPricing(
      resolvedForPricing,
      owner.priceDoc,
      pricingSegmentStore,
      splitPartnerAnalysisIDs
        ? { partnerAnalysisIDs: splitPartnerAnalysisIDs, insuranceCustomerAccountPriceDoc: insuranceCustomerAccountPriceDocForSplit }
        : undefined
    )

    // ── Write order document ─────────────────────────────────────────────────
    batch.set(orderRef, {
      id: orderRef.id,
      labAccountID: accountID,
      customer: owner.customer,
      customerAccountID: owner.customerAccountID,
      customerUserID: owner.customerUser.id,
      privateUserID: null,
      createdByUserID: userID,
      // Explicit null (not just absent) so `customerInvoiceID == null` queries — e.g. the
      // reconcile-stuck-orders sweep — match seeded orders the same as real ones.
      customerInvoiceID: null,
      partnerInvoiceID: null,
      segment: 'business' as const,
      orderNumber: blueprint.orderNumber,
      caseNumber: blueprint.caseNumber,
      address: {
        addressLine: 'Testvej 1',
        postalCode: '2100',
        city: 'København Ø',
        country: 'DK',
      },
      labPrefix: `L-${blueprint.orderNumber}`,
      statuses: {
        orderStatus,
        sampleRegistrationStatus: orderSampleRegistrationStatus,
        analysesDeliveryStatus: orderAnalysesDeliveryStatus,
        paymentStatus: orderPaymentStatus,
        labReportStatus: orderLabReportStatus,
        // Query index for the 08:00 scheduled-send job — see IOrderStatuses.
        hasScheduledReport: tatGroupList.some((g) => g.statuses.labReportStatus === 'scheduled'),
      },
      tatGroups,
      options: {
        isCreatedByLab: true,
        isAutomaticInvoicingDisabled: false,
        isInternal: false,
        isInsuranceCase,
        finalInvoicePayer,
      },
      insuranceNumber: isInsuranceCase ? (blueprint.insuranceNumber ?? null) : null,
      insurancePartnerID,
      reportTemplateAssignments,
      reportResponsibleUserAssignments,
      reportRecipientEmails: owner.customerUser.email ? [owner.customerUser.email] : [],
      sampleHandOver,
      dropPoint: dropPointSnapshot,
      dhlShipmentID: null,
      reportLocale: blueprint.reportLocale ?? 'da',
      samples: embeddedSamples,
      orderLines,
      orderTotals,
      orderDeals,
      timestamps: {
        createdAt: orderCreatedAt,
        updatedAt: orderCreatedAt,
        confirmedAt: orderCreatedAt,
        // Never set alongside isReadyToInvoice — orderStatus stays 'confirmed' for that
        // case (see the statuses block above), so completedAt would be a lie.
        completedAt: null,
        cancelledAt: allSamplesCancelled ? cancellationTime : null,
        firstSampleRegisteredAt: hasRegistered ? registrationTime : null,
        lastSampleRegisteredAt: hasRegistered ? registrationTime : null,
        // A real order stamps this on first registration; without it the cancel/unregister
        // services read `undefined` and Firestore rejects the write (MIL-412).
        tatStartDate: hasRegistered ? registrationTime : null,
        firstAnalysisResultReceivedAt: hasCompleted ? completionTime : null,
        lastAnalysisResultReceivedAt: hasCompleted ? completionTime : null,
        invoiceSentAt: null,
        invoiceDraftCreatedAt: null,
      },
      activityLog: [],
    })

    // ── Write sample documents ───────────────────────────────────────────────
    for (const rs of resolved) {
      batch.set(rs.sampleRef, {
        id: rs.sampleID,
        projectID: '',
        orderID: orderRef.id,
        labAccountID: accountID,
        customer: owner.customer,
        customerAccountID: owner.customerAccountID,
        createdByUserID: userID,
        sampleNumber: rs.sampleNumber,
        buildingAge: 20,
        analysisIDs: rs.analysisIDs,
        additionalAnalysisIDs: [],
        label: { id: rs.def.sampleCode, type: 'manual' as const },
        options: { isCreatedByLab: true },
        timestamps: { createdAt: orderCreatedAt, updatedAt: orderCreatedAt },
      })
    }
  })

  await batch.commit()
}
